/**
 * Simple utility functions for handling discount display based on discountAllocations
 */

/**
 * Calculate total discount amount from discountAllocations array
 * @param {Object} lineItem - Line item object with discount_allocations
 * @returns {number} Total discount amount
 */
export const calculateDiscountFromAllocations = (lineItem) => {
  if (!lineItem || !lineItem.discount_allocations || !Array.isArray(lineItem.discount_allocations)) {
    return 0;
  }

  return lineItem.discount_allocations.reduce((total, allocation) => {
    return total + parseFloat(allocation.amount || 0);
  }, 0);
};

/**
 * Check if item has any discount allocations
 * @param {Object} lineItem - Line item object
 * @returns {boolean} True if item has discounts
 */
export const hasDiscountAllocations = (lineItem) => {
  return lineItem && 
         lineItem.discount_allocations && 
         Array.isArray(lineItem.discount_allocations) && 
         lineItem.discount_allocations.length > 0;
};

/**
 * Calculate discounted price for a line item
 * @param {Object} lineItem - Line item object
 * @returns {number} Price after discount allocations applied
 */
export const calculateDiscountedPrice = (lineItem) => {
  if (!lineItem || !lineItem.price) {
    return 0;
  }

  const originalPrice = parseFloat(lineItem.price);
  const totalDiscount = calculateDiscountFromAllocations(lineItem);
  
  return Math.max(0, originalPrice - (totalDiscount / lineItem.quantity));
};

/**
 * Get discount breakdown for display purposes
 * @param {Object} lineItem - Line item object
 * @returns {Object} Discount information for display
 */
export const getDiscountInfo = (lineItem) => {
  const originalPrice = parseFloat(lineItem?.price || 0);
  const totalDiscount = calculateDiscountFromAllocations(lineItem);
  const hasDiscount = hasDiscountAllocations(lineItem);
  const discountedPrice = calculateDiscountedPrice(lineItem);

  return {
    originalPrice,
    totalDiscount,
    discountedPrice,
    hasDiscount,
    // Only show crossed out if there's an actual discount amount > 0
    shouldShowCrossedOut: totalDiscount > 0
  };
}; 